/**
 * Deployment model and network (contract revision 3, R3-A and R3-B, and
 * revision 4, R4-A).
 *
 *  - SaaS-Hybrid: shared DB + shared gateway, one VM (the app server). By
 *    default the network is the one recorded on the chosen SHARED DB SERVER;
 *    when any of its three values is blank the request is still created, with
 *    the network pending (filled in later in Django admin). Revision 4: the app
 *    server can join an EXISTING NETWORK recorded in Django admin instead.
 *  - Dedicated: own DB + own gateway, three VMs, and the customer's own network
 *    rg- / vnet- / snet-<region>-<env>-ww-<customer>, all lower case, e.g.
 *    rg-eastus2-prd-ww-stcroix (same rule as the backend). Revision 4: its VNet
 *    and subnet address ranges (CIDR) are typed in step 3.
 *
 * The names are never typed or sent: the backend resolves them on create. These
 * helpers only show them.
 *
 * Node-safe: relative imports only, no import.meta (the mock layer uses it).
 */
import { toAzureLocation } from '../../config/azureRegions'
import { DEPLOYMENT_MODEL_LABELS } from '../../config/workwise'
import type {
  DeploymentModel,
  DeploymentModelLabel,
  ExistingNetwork,
  NetworkSource,
  ServerMode,
  SharedServer,
  WorkwiseEnvironment,
  WorkwiseFormValues,
  WorkwiseSummary,
} from '../../types/workwise'
import { NETWORK_NAME_MAX, envToken, networkNameToken } from './naming'

/** The mode both the DB and the gateway take under a model. */
export function serverModeOf(model: DeploymentModel): ServerMode {
  return model === 'dedicated' ? 'dedicated' : 'shared'
}

/** The model a pair of modes describes; null for the (invalid) mixed topology. */
export function modelOfModes(db: ServerMode, gateway: ServerMode): DeploymentModel | null {
  if (db !== gateway) return null
  return db === 'dedicated' ? 'dedicated' : 'saas-hybrid'
}

/** VMs the request creates: the app server alone, or app + DB + gateway. */
export function vmCountOf(model: DeploymentModel): 1 | 3 {
  return model === 'dedicated' ? 3 : 1
}

/** The model label of a list row: the API's own, else derived from its modes (older rows). */
export function summaryModelLabel(ww: Pick<WorkwiseSummary, 'deployment_model' | 'db_mode' | 'gateway_mode'>): DeploymentModelLabel | null {
  if (ww.deployment_model) return ww.deployment_model
  const model = modelOfModes(ww.db_mode, ww.gateway_mode)
  return model ? DEPLOYMENT_MODEL_LABELS[model] : null
}

// ─── Network ─────────────────────────────────────────────────────────────────

export interface ResolvedNetwork {
  source: NetworkSource
  /** Blank while unknown: no customer name yet, no shared server picked, or pending. */
  resourceGroup: string
  vnet: string
  subnet: string
  /** SaaS-Hybrid: the picked shared DB server has no recorded network yet. */
  pending: boolean
  /** SaaS-Hybrid: the shared DB server the network comes from (null until one is picked, or with an existing network). */
  serverName: string | null
  /** Dedicated (R4-A): the address ranges typed in step 3, trimmed; blank otherwise. */
  vnetCidr: string
  subnetCidr: string
  /** SaaS-Hybrid with an existing network chosen (R4): which one; null otherwise. */
  existingNetwork: { id: number; name: string; addressSpace: string } | null
}

/** No address ranges and no existing network: the revision-3 shapes. */
const NO_R4_CHOICE = { vnetCidr: '', subnetCidr: '', existingNetwork: null }

/** Who a Dedicated network belongs to: its names are built from these. */
export interface NetworkOwner {
  customerName: string
  /** Display name ("East US 2") or location code ("eastus2"). */
  region: string
  environment: WorkwiseEnvironment
}

/**
 * Dedicated: the customer's own rg-/vnet-/snet-<region>-<env>-ww-<customer>, all lower case
 * ("St Croix" in East US 2 production -> rg-eastus2-prd-ww-stcroix). The customer part is shortened so the
 * VNet name stays within 64 characters. Blank until the customer name has a letter or digit.
 */
export function customerNetwork({ customerName, region, environment }: NetworkOwner): ResolvedNetwork {
  const location = toAzureLocation(region)
  const stem = `${location}-${envToken(environment)}-ww-`
  const token = networkNameToken(customerName).slice(0, NETWORK_NAME_MAX - 'vnet-'.length - stem.length)
  const ready = token !== '' && location !== ''
  return {
    source: 'customer',
    resourceGroup: ready ? `rg-${stem}${token}` : '',
    vnet: ready ? `vnet-${stem}${token}` : '',
    subnet: ready ? `snet-${stem}${token}` : '',
    pending: false,
    serverName: null,
    ...NO_R4_CHOICE,
  }
}

type NetworkFields = Pick<SharedServer, 'name' | 'network_resource_group' | 'vnet' | 'subnet' | 'network_recorded'>

/** True when the shared server has all three network values recorded. */
export function hasRecordedNetwork(server: Partial<NetworkFields>): boolean {
  if (server.network_recorded === false) return false
  return [server.network_resource_group, server.vnet, server.subnet].every((v) => typeof v === 'string' && v.trim() !== '')
}

/** SaaS-Hybrid: the shared DB server's network, or pending (blank values) when it is not recorded. */
export function sharedServerNetwork(server: NetworkFields | null | undefined): ResolvedNetwork {
  const empty = { source: 'shared_server' as const, resourceGroup: '', vnet: '', subnet: '', ...NO_R4_CHOICE }
  if (!server) return { ...empty, pending: false, serverName: null }
  if (!hasRecordedNetwork(server)) return { ...empty, pending: true, serverName: server.name }
  return {
    ...empty,
    resourceGroup: server.network_resource_group.trim(),
    vnet: server.vnet.trim(),
    subnet: server.subnet.trim(),
    pending: false,
    serverName: server.name,
  }
}

type ExistingNetworkFields = Pick<ExistingNetwork, 'id' | 'name' | 'resource_group' | 'vnet' | 'subnet' | 'address_space'>

/** An existing network's label: its name, or its VNet when the admin left the name blank (R4-B). */
export function existingNetworkName(network: Pick<ExistingNetwork, 'name' | 'vnet'>): string {
  return network.name.trim() || network.vnet.trim()
}

/** "<name> — <vnet> / <subnet> (<resource group>)": an option of the "App server network" select. */
export function existingNetworkLabel(network: ExistingNetworkFields): string {
  return `${existingNetworkName(network)} — ${network.vnet} / ${network.subnet} (${network.resource_group})`
}

/** SaaS-Hybrid with an existing network chosen (R4): the app server joins it; never pending. */
export function existingNetworkOf(network: ExistingNetworkFields): ResolvedNetwork {
  return {
    source: 'existing_network',
    resourceGroup: network.resource_group.trim(),
    vnet: network.vnet.trim(),
    subnet: network.subnet.trim(),
    pending: false,
    serverName: null,
    vnetCidr: '',
    subnetCidr: '',
    existingNetwork: { id: network.id, name: existingNetworkName(network), addressSpace: network.address_space.trim() },
  }
}

/** What the user chose in the Network section (revision 4). */
export interface NetworkChoice {
  /** Dedicated: the VNet and subnet address ranges. */
  vnetCidr?: string
  subnetCidr?: string
  /** SaaS-Hybrid: the existing network picked instead of the shared DB server's (null/undefined = none). */
  existing?: ExistingNetworkFields | null
}

/** The network a request will get, as the backend resolves it on create. */
export function resolveNetwork(
  model: DeploymentModel,
  owner: NetworkOwner,
  sharedDb?: NetworkFields | null,
  choice: NetworkChoice = {},
): ResolvedNetwork {
  if (model === 'dedicated') {
    return { ...customerNetwork(owner), vnetCidr: (choice.vnetCidr ?? '').trim(), subnetCidr: (choice.subnetCidr ?? '').trim() }
  }
  return choice.existing ? existingNetworkOf(choice.existing) : sharedServerNetwork(sharedDb)
}

/** The lookups the form refers to by id: the shared DB server and the existing network (if any). */
export interface NetworkPicks {
  db?: NetworkFields | null
  network?: ExistingNetworkFields | null
}

/** The network of the wizard form: owner and address ranges from its values, the picks from the lookups. */
export function formNetwork(values: WorkwiseFormValues, picks: NetworkPicks = {}): ResolvedNetwork {
  const owner = { customerName: values.customer.name, region: values.region, environment: values.environment }
  return resolveNetwork(values.deploymentModel, owner, picks.db, {
    vnetCidr: values.network?.vnetCidr,
    subnetCidr: values.network?.subnetCidr,
    existing: picks.network,
  })
}
